<template>
  <div>
    <h2>{{ t('listingDocuments.title') }} ({{ listingDocuments?.length ?? 0 }})</h2>
    <ul v-if="hasListingDocuments" :class="$style.links">
      <li
        v-for="listingDocument in listingDocuments"
        :key="listingDocument.href"
        :class="$style.linkItem"
      >
        <HgLink
          :icon="listingDocument.icon"
          :href="listingDocument.href"
          rel="noopener noreferrer"
          target="_blank"
          @click="onDocumentClick(listingDocument)"
        >
          {{ listingDocument.label }}
        </HgLink>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { HgLink } from '@smg-real-estate/domus';
import { L10N } from '@smg-real-estate/hg-rets-schema';
import { computed } from 'vue';

import {
  getDetectedFloorPlansForLocale,
  getListingAttachedDocument,
} from '@/helpers/ListingData/ListingData';
import { getNumberedLabel } from '@/helpers/getNumberedLabel';
import { useI18n } from 'vue-i18n';

interface ListingDocumentItem {
  href?: string;
  label: string;
  icon: 'Document' | 'FloorPlan';
  isFloorPlan: boolean;
}

const { t } = useI18n();

defineOptions({
  name: 'ListingDocuments',
});

interface Props {
  listingDefaultLocale: L10N | undefined;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  documentClick: [];
  floorPlanClick: [];
}>();

const onDocumentClick = (listingDocument: ListingDocumentItem): void => {
  if (listingDocument.isFloorPlan) {
    emit('floorPlanClick');
  } else {
    emit('documentClick');
  }
};

const hasListingDocuments = computed((): boolean => {
  return !!listingDocuments.value && listingDocuments.value.length > 0;
});

const listingDocuments = computed((): ListingDocumentItem[] | undefined => {
  if (!props.listingDefaultLocale) {
    return undefined;
  }

  const allDocuments = getListingAttachedDocument(props.listingDefaultLocale);
  const detectedFloorPlans = getDetectedFloorPlansForLocale(props.listingDefaultLocale);
  const floorPlanDocumentCount = detectedFloorPlans.documents.length;

  const floorPlanDocuments: ListingDocumentItem[] = detectedFloorPlans.documents.map(
    (document, index) => ({
      href: document.href,
      label: getNumberedLabel(t('floorPlan.documentLabel'), floorPlanDocumentCount, index),
      icon: 'FloorPlan',
      isFloorPlan: true,
    }),
  );

  const otherDocuments: ListingDocumentItem[] = allDocuments
    .filter(
      (document) =>
        !detectedFloorPlans.documents.some(
          (floorPlanDocument) => floorPlanDocument.href === document.href,
        ),
    )
    .map((document) => ({
      href: document.href,
      label: document.label || t('buttons.attachedDocument'),
      icon: 'Document',
      isFloorPlan: false,
    }));

  return [...floorPlanDocuments, ...otherDocuments];
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

$link-min-width: 70px;
$link-min-width-vp-large: 55px;
$link-label-max-width: 200px;
$listing-accessories-min-height: 2em;
$item-max-width: 460px;

.links {
  display: flex;
  flex-flow: column nowrap;
  gap: $space-75;
  max-width: 100%;
  min-height: $listing-accessories-min-height;

  @include menu-ul-reset;
}

.linkItem {
  @include body-2;

  min-width: $link-min-width;
  vertical-align: middle;

  a {
    span {
      display: block;
      max-width: $item-max-width;

      @include truncate-text;
    }
  }
}
</style>
